// React Imports
import { ReactElement, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

// Third-Party Library Imports
import { useDispatch, useSelector } from "react-redux";

// Local Component Imports
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";

// Service Imports
import { CREATE_BREADCRUMB } from "../../store/actions";
import TabsComponent from "src/components/tabs-component/tabs-component";
import VerifiedList from "./verified-list/verified-list";
import PendingList from "./pending-list/pending-list";
import AllCustomersList from "./all-customers-list/all-customers-list";
import CustomerService from "./customer.service";
import { Dropdown, MenuProps } from "antd";
import { DownOutlined } from "@ant-design/icons";

/**
 * Drops the separators a hidden item leaves behind.
 *
 * Dividers are unconditional entries in the items array while the items either
 * side are permission-gated, so a user without a permission can be left with a
 * separator that leads, trails, or doubles up with nothing between. Falsy
 * entries (a failed gate) are removed first, then any divider that is no
 * longer sitting between two real items.
 */
const compactMenuItems = (items: any[]): MenuProps["items"] => {
  const kept: any[] = [];
  items.filter(Boolean).forEach((item) => {
    const isDivider = item?.type === "divider";
    if (
      isDivider &&
      (kept.length === 0 || kept[kept.length - 1]?.type === "divider")
    ) {
      return;
    }
    kept.push(item);
  });
  while (kept.length && kept[kept.length - 1]?.type === "divider") kept.pop();
  return kept;
};

const CustomersList: React.FC = (): ReactElement => {
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const customerServiceInstance = new CustomerService();
  const user = useSelector((state: any) => state.user);
  const [activeTab, setActiveTab] = useState<string>(
    queryParams.get("tab") || "1"
  );

  useEffect(() => {
    dispatch({
      type: CREATE_BREADCRUMB,
      payload: {
        breadcrumb: [
          {
            text: `Customers list`,
            type: "link",
            link: "/home/customers-list",
          },
        ],
      },
    });
  }, []);

  const items: any = [
    {
      key: "1",
      label: `Verified`,
      children: <VerifiedList />,
    },
    {
      key: "2",
      label: `Pending`,
      children: <PendingList />,
    },
    {
      key: "3",
      label: `All`,
      children: <AllCustomersList />,
    },
  ];

  const onTabChange = (activeKey: string): void => {
    setActiveTab(activeKey);
  };

  const accountManagementItems: MenuProps["items"] = compactMenuItems([
    user?.permissions?.includes("onboard_new_customer") && {
      key: "1",
      label: (
        <Link to="/customers/upload" className="flex items-center gap-2">
          <img
            src="/images/onboarding-icon.svg"
            alt="send message"
            className="w-5 h-5"
          />
          <span className="text-sm">Customers onboarding</span>
        </Link>
      ),
    },
    {
      type: "divider",
    },
    user?.permissions?.includes("add_saudi_kyc_backoffice") && {
      key: "2",
      label: (
        <Link to="/home/create-customer" className="flex items-center gap-2">
          <img
            src="/images/new-account-icon.svg"
            alt="send message"
            className="w-5 h-5"
          />
          <span className="text-sm">New account</span>
        </Link>
      ),
    },
    {
      type: "divider",
    },
    // Same gate as "Online trade access" in the sidebar
    // (components/menu/menu.component.tsx) — both are online-trading tools.
    user?.permissions?.includes("online_trading_trx_ips") && {
      key: "3",
      label: (
        <Link
          to="/customers/transaction-report-ip"
          className="flex items-center gap-2"
        >
          <img
            src="/images/transactions-ip-icon.svg"
            alt=""
            className="w-5 h-5"
          />
          <span className="text-sm">Trading transaction IPs</span>
        </Link>
      ),
    },
  ]);

  return (
    <div className="flex flex-col customers-table">
      <div className="flex justify-between items-center">
        <BreadcrumbComponent />

        <Dropdown menu={{ items: accountManagementItems }}>
          <a
            onClick={(e) => e.preventDefault()}
            className="flex items-center gap-2 cursor-pointer bg-blue-500 px-4 py-2 rounded-full text-sm"
          >
            <p className="text-sm text-white">Account Management</p>
            <DownOutlined className="text-white" />
          </a>
        </Dropdown>
      </div>
      <TabsComponent
        items={items}
        activeTab={activeTab}
        onChange={onTabChange}
      />
    </div>
  );
};

export default CustomersList;
